import { getActiveLocaleSelector, getIsLoggedInSelector, getTenancySelector } from '@americanexpress/travel-selectors';
import { addErrorToReport, loadLanguagePack } from '@americanexpress/one-app-ducks';
import { composeModules } from 'holocron';
import { isConditionalFeature } from '@americanexpress/conditional-features';
import { oneErrorOnNotOkFetchye, replaceDomainForCacheKey } from '@americanexpress/fetchye-amex';
import { getPrefetchedExperimentsSelector } from '../selectors/getPrefetchedExperimentsSelector';
import { DISABLE_PACKAGES_FF, DISPLAY_E_TICKET_BANNER } from '../constants/oneXpConstants';
import { loadMembershipDomain } from './loadMembership';
import {
  MODULE_NAME, SEARCH_MODULE_NAME, TERMS_MODULE_NAME, ADVISORY_MODULE_NAME,
  UNUSED_TICKETS_MODULE_NAME, MARKETING_MODULE_NAME, CREDIT_MODULE_NAME,
  MR_MODULE_NAME, LEGAL_MODULE_NAME, FOOTER_MODULE_NAME,
} from '../constants/modules';
import { oneCmsReporting } from '../fetchye/useOneCmsFetchye';
import {
  hasFooterLinksRule, hasLegalRule, hasMultipleRewardsRule,
  hasProjectAdsRule, hasTravelCreditOverlayRule, hasUnusedCreditRule,
} from '../constants/featureRulesConstants';
import getLineOfBusinessFromPath from '../utils/getLineOfBusinessUtils';
import getIsWaitingForMultipleRewardsSelector from '../selectors/getIsWaitingForMultipleRewardsSelector';
import { getOneCmsFilePath } from '../selectors/getOneCmsJsonSelectors';

export const loadModuleData = async ({ store, ownProps }) => {
  const { dispatch, getState } = store;
  const state = getState();
  const tenancy = getTenancySelector(state);
  const locale = getActiveLocaleSelector(state);
  const isLoggedIn = getIsLoggedInSelector(state);
  const oneCmsUrl = getOneCmsFilePath(getState());
  const experiments = getPrefetchedExperimentsSelector(state);
  const isWaitingForMultipleRewards = getIsWaitingForMultipleRewardsSelector(state);
  const isPackagesDisabledByFF = experiments
    ?.[DISABLE_PACKAGES_FF]?.dimensions?.[tenancy]?.includes(locale);
  const isETicketBannerDisplayedByFF = experiments
    ?.[DISPLAY_E_TICKET_BANNER]?.dimensions?.[tenancy]?.includes(locale);

  const activeTab = getLineOfBusinessFromPath(
    isPackagesDisabledByFF,
    ownProps?.location?.pathname
  );

  try {
    const resolution = await Promise.allSettled([
      dispatch(loadMembershipDomain(state)),
      dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' })),
      dispatch(oneErrorOnNotOkFetchye(`${oneCmsUrl}homepage.json`, {
        mapKeyToCacheKey: replaceDomainForCacheKey('oneCMS'),
      }, oneCmsReporting)),
      dispatch(oneErrorOnNotOkFetchye(`${oneCmsUrl}travel-homepage.json`, {
        mapKeyToCacheKey: replaceDomainForCacheKey('oneCMS'),
      }, oneCmsReporting)),
      dispatch(composeModules([
        {
          name: SEARCH_MODULE_NAME,
          props: { activeTab },
        },
        {
          name: TERMS_MODULE_NAME,
          props: { programs: ['homepage'] },
        },
        {
          name: ADVISORY_MODULE_NAME,
        },
        ...isConditionalFeature(hasUnusedCreditRule, state)
        && isLoggedIn && isETicketBannerDisplayedByFF ? [{
            name: UNUSED_TICKETS_MODULE_NAME,
          }] : [],
        ...isConditionalFeature(hasProjectAdsRule, state) ? [{
          name: MARKETING_MODULE_NAME,
        }] : [],
        ...isConditionalFeature(hasTravelCreditOverlayRule, state) ? [{
          name: CREDIT_MODULE_NAME,
          props: { isWaitingForMultipleRewards, isOverlay: true },
        }] : [],
        ...isConditionalFeature(hasMultipleRewardsRule, state) ? [{
          name: MR_MODULE_NAME,
        }] : [],
        ...isConditionalFeature(hasLegalRule, state) ? [{
          name: LEGAL_MODULE_NAME,
        }] : [],
        ...isConditionalFeature(hasFooterLinksRule, state) ? [{
          name: FOOTER_MODULE_NAME,
        }] : [],
      ]))]
    );
    await Promise.all(resolution.filter((result) => result.status === 'rejected')
      .map(async (result) => {
        dispatch(addErrorToReport(result.reason, {
          collectionMethod: 'loadModuleData rejected',
          moduleID: MODULE_NAME,
          caught: true,
        }));
      }));
  } catch (e) {
    dispatch(addErrorToReport(e, {
      collectionMethod: 'loadModuleData error',
      moduleID: MODULE_NAME,
      caught: false,
    }));
  }
};
